'use strict';
import forEach from 'lodash/forEach';
import omit from 'lodash/omit';
import {LOAD} from 'redux-storage';
import {GpsStatus} from '../../core-utilities/enums/GpsStatus';
import * as trackLocationActions from '../actions/trackLocationActions';
import {getTrackLocationSafe} from './state';

/**
 * @typedef {Object} TrackLocationState
 * @property {GpsStatus} gpsStatus
 * @property {Object<String, ChangeTrackingPayload>} listeners The current state keys requesting GPS data
 * @property {Number} size The size of listeners
 * @property {?UpdatePositionPayload} lastPositionPayload
 */

/** @type {TrackLocationState} */
export const initialState = {
  gpsStatus: GpsStatus.STOPPED,
  listeners: {},
  size: 0,
  lastPositionPayload: null,
};

/**
 *
 * @param {TrackLocationState} state
 * @param {ReduxAction} action
 * @return {TrackLocationState}
 */
export function trackLocation(state = initialState, action) {
  switch (action.type) {
    case trackLocationActions.startTracking.TYPE:
      return startTrackingReducer(state, action);

    case trackLocationActions.stopTracking.TYPE:
      return stopTrackingReducer(state, action);

    case trackLocationActions.resetTrackingCache.TYPE:
      return resetTrackingCacheReducer(state, action);

    case trackLocationActions.registerTracking.TYPE:
      return registerTrackingReducer(state, action);

    case trackLocationActions.deregisterTracking.TYPE:
      return deregisterTrackingReducer(state, action);

    case trackLocationActions.updatePosition.TYPE:
      return updatePositionReducer(state, action);

    case LOAD:
      return loadReducer(state, action);

    default:
      return state;
  }
}

/**
 * @param {TrackLocationState} state
 * @param {ReduxAction<Void>} action
 * @return {TrackLocationState}
 */
function startTrackingReducer(state, action) {
  if (!state.gpsStatus || state.gpsStatus === GpsStatus.STOPPED) {
    return {
      ...state,
      gpsStatus: GpsStatus.REQUESTING,
    };
  } else {
    return state;
  }
}

/**
 * @param {TrackLocationState} state
 * @param {ReduxAction<Void>} action
 * @return {TrackLocationState}
 */
function stopTrackingReducer(state, action) {
  if (state.size === 0) {
    return {
      ...state,
      gpsStatus: GpsStatus.STOPPED,
    };
  } else {
    return state;
  }
}

/**
 * Remove the cached position if the status is stopped.
 * @param {TrackLocationState} state
 * @param {ReduxAction<Void>} action
 * @return {TrackLocationState}
 */
function resetTrackingCacheReducer(state, action) {
  if (state.gpsStatus === GpsStatus.STOPPED && state.lastPositionPayload) {
    return {
      ...state,
      lastPositionPayload: null,
    };
  } else {
    return state;
  }
}

/**
 * @param {TrackLocationState} state
 * @param {ReduxAction<ChangeTrackingPayload>} action
 * @return {TrackLocationState}
 */
function registerTrackingReducer(state, action) {
  const listeners = {
    ...state.listeners,
    [action.payload.key]: action.payload,
  };
  return {
    ...state,
    listeners,
    size: Object.keys(listeners).length,
  };
}

/**
 * @param {TrackLocationState} state
 * @param {ReduxAction<ChangeTrackingPayload>} action
 * @return {TrackLocationState}
 */
function deregisterTrackingReducer(state, action) {
  const listeners = omit(state.listeners, [action.payload.key]);
  return {
    ...state,
    listeners,
    size: Object.keys(listeners).length,
  };
}

/**
 * @param {TrackLocationState} state
 * @param {ReduxAction<UpdatePositionPayload>} action
 * @return {TrackLocationState}
 */
function updatePositionReducer(state, action) {
  // ignore GpsStatus.STOPPED empty position data
  return action.payload.gpsStatus === GpsStatus.STOPPED ? state : {
    ...state,
    // if any registered items are once: true, deregister them
    ...omitKeys(state, getOnceKeys(state)),
    gpsStatus: action.payload.gpsStatus,
    lastPositionPayload: action.payload,
  };
}

/**
 * on redux-storage LOAD, reset the gps status to the initialState value.
 * @param {TrackLocationState} state
 * @param {ReduxAction} action
 * @return {TrackLocationState}
 */
function loadReducer(state, action) {
  const cachedState = getTrackLocationSafe(action.payload);
  if (cachedState && cachedState.listeners) {
    return {
      ...state,
      ...cachedState,
      // if any registered items are resumeOnLoad: false, deregister them
      ...omitKeys(cachedState, getNoResumeKeys(cachedState)),
      gpsStatus: initialState.gpsStatus,
      lastPositionPayload: null,
    };
  } else {
    return state;
  }
}

/**
 * Remove the specified keys from state.listeners and update size.
 * Does not mutate state.
 *
 * @param {TrackLocationState} state
 * @param {Array<String>} keys
 * @return {TrackLocationState}
 */
function omitKeys(state, keys) {
  if (keys.length > 0) {
    const listeners = omit(state.listeners, keys);
    return {
      listeners,
      size: Object.keys(listeners).length,
    };
  } else {
    return {};
  }
}

/**
 * @param {TrackLocationState} state
 * @return {Array<ChangeTrackingPayload>}
 */
function getOnceKeys(state) {
  const keys = [];
  forEach(state.listeners, listener => {
    if (listener.once) {
      keys.push(listener.key);
    }
  });

  return keys;
}

/**
 * @param {TrackLocationState} state
 * @return {Array<ChangeTrackingPayload>}
 */
function getNoResumeKeys(state) {
  const keys = [];
  forEach(state.listeners, listener => {
    if (!listener.resumeOnLoad) {
      keys.push(listener.key);
    }
  });

  return keys;
}



// WEBPACK FOOTER //
// ./src/js/settings/reducers/trackLocationReducers.js